"use client";

import { useId, useMemo, useState } from "react";

type Category = "letters" | "decorated" | "frames";

type StyleDefinition = {
  id: string;
  label: string;
  category: Category;
  transform: (text: string) => string;
};

/* -------------------------------- Helpers -------------------------------- */

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";

function buildRange(chars: string, startCodePoint: number): Record<string, string> {
  const map: Record<string, string> = {};
  [...chars].forEach((char, index) => {
    map[char] = String.fromCodePoint(startCodePoint + index);
  });
  return map;
}
function mapWith(...tables: Record<string, string>[]) {
  return Object.assign({}, ...tables) as Record<string, string>;
}
function applyMap(text: string, map: Record<string, string>): string {
  return [...text].map((char) => map[char] ?? char).join("");
}

/* --------------------- Mathematical Alphanumeric Symbols --------------------- */

const BOLD = mapWith(
  buildRange(UPPER, 0x1d400),
  buildRange(LOWER, 0x1d41a),
  buildRange(DIGITS, 0x1d7ce),
);
const BOLD_SANS = mapWith(
  buildRange(UPPER, 0x1d5d4),
  buildRange(LOWER, 0x1d5ee),
  buildRange(DIGITS, 0x1d7ec),
);
const ITALIC = mapWith(
  buildRange(UPPER, 0x1d434),
  Object.fromEntries(
    [...LOWER].map((char, i) =>
      char === "h" ? ["h", "\u210E"] : [char, String.fromCodePoint(0x1d44e + i)],
    ),
  ),
);
const SCRIPT = mapWith(
  { B: "\u212C", E: "\u2130", F: "\u2131", H: "\u210B", I: "\u2110", L: "\u2112", M: "\u2133", R: "\u211B" },
  Object.fromEntries(
    [...UPPER]
      .filter((c) => !"BEFHILMR".includes(c))
      .map((char) => [char, String.fromCodePoint(0x1d49c + UPPER.indexOf(char))]),
  ),
  { e: "\u212F", g: "\u210A", o: "\u2134" },
  Object.fromEntries(
    [...LOWER]
      .filter((c) => !"ego".includes(c))
      .map((char) => [char, String.fromCodePoint(0x1d4b6 + LOWER.indexOf(char))]),
  ),
);
const SCRIPT_BOLD = mapWith(buildRange(UPPER, 0x1d4d0), buildRange(LOWER, 0x1d4ea));
const FRAKTUR = mapWith(
  { C: "\u212D", H: "\u210C", I: "\u2111", R: "\u211C", Z: "\u2128" },
  Object.fromEntries(
    [...UPPER]
      .filter((c) => !"CHIRZ".includes(c))
      .map((char) => [char, String.fromCodePoint(0x1d504 + UPPER.indexOf(char))]),
  ),
  buildRange(LOWER, 0x1d51e),
);
const DOUBLE_STRUCK = mapWith(
  { C: "\u2102", H: "\u210D", N: "\u2115", P: "\u2119", Q: "\u211A", R: "\u211D", Z: "\u2124" },
  Object.fromEntries(
    [...UPPER]
      .filter((c) => !"CHNPQRZ".includes(c))
      .map((char) => [char, String.fromCodePoint(0x1d538 + UPPER.indexOf(char))]),
  ),
  buildRange(LOWER, 0x1d552),
  buildRange(DIGITS, 0x1d7d8),
);

/* ------------------------------ Legacy blocks ------------------------------ */

// Small caps — scattered legacy IPA/phonetic block. 'q' and 'x' have no true
// small-caps glyph; they fall back to the plain lowercase letter.
const SMALL_CAPS: Record<string, string> = {
  a: "ᴀ", b: "ʙ", c: "ᴄ", d: "ᴅ", e: "ᴇ", f: "ꜰ", g: "ɢ", h: "ʜ", i: "ɪ",
  j: "ᴊ", k: "ᴋ", l: "ʟ", m: "ᴍ", n: "ɴ", o: "ᴏ", p: "ᴘ", r: "ʀ", s: "ꜱ",
  t: "ᴛ", u: "ᴜ", v: "ᴠ", w: "ᴡ", y: "ʏ", z: "ᴢ",
};

const BUBBLE = mapWith(
  buildRange(UPPER, 0x24b6),
  buildRange(LOWER, 0x24d0),
  { 0: "\u24ea" },
  buildRange("123456789", 0x2460),
);
// Digits have no filled-circle glyph in this block; left unmapped by design.
const BUBBLE_FILLED: Record<string, string> = Object.fromEntries(
  [...UPPER].map((char, i) => [char, String.fromCodePoint(0x1f150 + i)]),
);
const SQUARED: Record<string, string> = Object.fromEntries(
  [...UPPER].map((char, i) => [char, String.fromCodePoint(0x1f130 + i)]),
);
const SQUARED_FILLED: Record<string, string> = Object.fromEntries(
  [...UPPER].map((char, i) => [char, String.fromCodePoint(0x1f170 + i)]),
);

const FULLWIDTH = mapWith(
  buildRange(UPPER, 0xff21),
  buildRange(LOWER, 0xff41),
  buildRange(DIGITS, 0xff10),
  { " ": "\u3000" },
);

const UPSIDE_DOWN_MAP: Record<string, string> = {
  a: "ɐ", b: "q", c: "ɔ", d: "p", e: "ǝ", f: "ɟ", g: "ƃ", h: "ɥ", i: "ᴉ",
  j: "ɾ", k: "ʞ", l: "l", m: "ɯ", n: "u", o: "o", p: "d", q: "b", r: "ɹ",
  s: "s", t: "ʇ", u: "n", v: "ʌ", w: "ʍ", x: "x", y: "ʎ", z: "z",
  A: "∀", B: "B", C: "Ɔ", D: "D", E: "Ǝ", F: "Ⅎ", G: "פ", H: "H", I: "I",
  J: "ſ", K: "K", L: "˥", M: "W", N: "N", O: "O", P: "Ԁ", Q: "Q", R: "R",
  S: "S", T: "┴", U: "∩", V: "Λ", W: "M", X: "X", Y: "⅄", Z: "Z",
  "0": "0", "1": "⇂", "2": "ᄅ", "3": "Ɛ", "4": "ㄣ", "5": "ϛ", "6": "9",
  "7": "ㄥ", "8": "8", "9": "6", ".": "˙", ",": "'", "'": ",", "?": "¿",
  "!": "¡", "(": ")", ")": "(",
};

// Visual lookalike scripts — chosen purely for resemblance to Latin letters,
// not actual words in these languages.
const CYRILLIC_LOOKALIKE: Record<string, string> = {
  a: "α", b: "Ь", c: "ς", d: "ԁ", e: "є", f: "ƒ", g: "ɠ", h: "н", i: "ι",
  j: "נ", k: "κ", l: "ʅ", m: "м", n: "ภ", o: "σ", p: "ρ", q: "ợ", r: "г",
  s: "ร", t: "τ", u: "υ", v: "ש", w: "ฬ", x: "א", y: "ץ", z: "z",
};
const GREEK_LOOKALIKE: Record<string, string> = {
  a: "α", b: "в", c: "ƈ", d: "ԃ", e: "ҽ", f: "ϝ", g: "ɠ", h: "ԋ", i: "ι",
  j: "ʝ", k: "ƙ", l: "ʅ", m: "ɱ", n: "ɳ", o: "σ", p: "ρ", q: "۹", r: "ɾ",
  s: "ʂ", t: "ƚ", u: "υ", v: "ʋ", w: "ɯ", x: "x", y: "ყ", z: "ȥ",
};
const THAI_LOOKALIKE: Record<string, string> = {
  a: "α", b: "b", c: "¢", d: "ԃ", e: "e", f: "f", g: "g", h: "h", i: "i",
  j: "j", k: "k", l: "l", m: "m", n: "ຖ", o: "໐", p: "p", q: "q", r: "r",
  s: "Ş", t: "t", u: "u", v: "v", w: "ຟ", x: "x", y: "y", z: "z",
};

/* --------------------------- Combining-mark styles --------------------------- */

// Wraps EACH letter with a combining mark (keycap circle, box, etc).
// A hair-space (U+200A) between letters keeps consecutive marks from
// visually colliding.
function withCombining(mark: string) {
  return (text: string) =>
    [...text].map((c) => (c === " " ? " " : c + mark)).join("\u200a");
}

// Zalgo / glitch text: stacks random diacritics above/below each character.
// Randomized per call — click "Reroll" to get a new pattern on the same text.
const ZALGO_ABOVE = [
  "\u0300", "\u0301", "\u0302", "\u0303", "\u0304", "\u0305", "\u0306", "\u0307",
  "\u0308", "\u0309", "\u030a", "\u030b", "\u030c", "\u0310", "\u0312", "\u033d",
];
const ZALGO_BELOW = [
  "\u0316", "\u0317", "\u0318", "\u0319", "\u031c", "\u031d", "\u031e", "\u031f",
  "\u0320", "\u0324", "\u0325", "\u0326", "\u032e", "\u0330", "\u0333",
];
function zalgofy(text: string, intensity: number): string {
  return [...text]
    .map((char) => {
      if (char === " " || char === "\n") return char;
      let result = char;
      const aboveCount = Math.floor(Math.random() * intensity) + 1;
      const belowCount = Math.floor(Math.random() * intensity);
      for (let i = 0; i < aboveCount; i += 1) {
        result += ZALGO_ABOVE[Math.floor(Math.random() * ZALGO_ABOVE.length)];
      }
      for (let i = 0; i < belowCount; i += 1) {
        result += ZALGO_BELOW[Math.floor(Math.random() * ZALGO_BELOW.length)];
      }
      return result;
    })
    .join("");
}

/* ------------------------------ Frame wrappers ------------------------------ */
/* Whole-string decoration: prefix/suffix concatenation, not per-character
   substitution. Best for bios and captions, not usernames (these run long). */

const FRAMES: { id: string; label: string; wrap: (text: string) => string }[] = [
  { id: "crownGift", label: "Crown & Gift", wrap: (t) => `♔🎁  ${t}  😾♕` },
  { id: "aestheticFrame", label: "Aesthetic Frame", wrap: (t) => `˚₊‧꒰ა ${t} ໒꒱‧₊˚` },
  { id: "starBurst", label: "Star Burst", wrap: (t) => `★彡${t}彡★` },
  { id: "bow", label: "Bow", wrap: (t) => `🎀  ${t}  🎀` },
  { id: "squareBrackets", label: "Square Brackets", wrap: (t) => `【${t}】` },
  { id: "cornerBrackets", label: "Corner Brackets", wrap: (t) => `『${t}』` },
  { id: "diamonds", label: "Diamond Frame", wrap: (t) => `◆◇◆ ${t} ◆◇◆` },
  { id: "waveDots", label: "Wave & Dots", wrap: (t) => `≋${[...t].join("≋")}≋` },
  { id: "blockShade", label: "Block Shade", wrap: (t) => `░${[...t].join("░")}░` },
  { id: "kawaiiFace", label: "Kawaii Face", wrap: (t) => `(っ◔◡◔)っ ♥ ${t} ♥` },
  { id: "fullwidthShade", label: "Fullwidth Shade", wrap: (t) => `${applyMap(t, FULLWIDTH)}░${applyMap(t, FULLWIDTH)}` },
  { id: "flourish", label: "Flourish", wrap: (t) => `(¯\`·.¸¸.·´¯\`·.¸¸.-> ${t} <-.¸¸.·´¯\`·.¸¸.·´¯)` },
  { id: "royalSeal", label: "Royal Seal", wrap: (t) => `๑۞๑,¸¸,ø¤º°\`°๑۩ ${t} ๑۩ ,¸¸,ø¤º°\`°๑۞๑` },
  { id: "ornateLine", label: "Ornate Line", wrap: (t) => `ஜ۩۞۩ஜ ${t} ஜ۩۞۩ஜ` },
  { id: "gamerTag", label: "Gamer Tag", wrap: (t) => `☆꧁༒☬${t}☬༒꧂☆` },
  { id: "equalizer", label: "Equalizer Bars", wrap: (t) => `▌│█║▌║▌║ ${t} ║▌║▌║█│▌` },
  { id: "keycapBoxed", label: "Boxed (keycap style)", wrap: (t) => [...t].map((c) => (c === " " ? " " : `[̲̅${c}]`)).join("") },
  { id: "petals", label: "Petals", wrap: (t) => `*´¯\`*.¸¸.*´¯\`*   🎀  ${t}  🎀   *\`¯´*.¸¸.*\`¯´*` },
  { id: "sparkleRing", label: "Sparkle Ring", wrap: (t) => `✮✧  🎀  ${t}  🎀  ✧✮` },
  { id: "sheepHamster", label: "Sheep & Hamster", wrap: (t) => `🐑 ⋆ 🐹  🎀  ${t}  🎀  🐹 ⋆ 🐑` },
  { id: "bunnySquirrel", label: "Bunny & Squirrel", wrap: (t) => `🐇 ⋆ 🐿  🎀  ${t}  🎀  🐿 ⋆ 🐇` },
  { id: "candyHeart", label: "Candy Heart", wrap: (t) => `💣🍔  ${t}  ☹🐧` },
  { id: "crownKing", label: "Crown & King", wrap: (t) => `👺👹  ${t}  😳👑` },
  { id: "cloverGreen", label: "Clover", wrap: (t) => `👹🐧  ${t}  🎀💚` },
  { id: "pumpkinOwl", label: "Pumpkin & Owl", wrap: (t) => `🎃🍔  ${t}  🐨👌` },
];

/* --------------------------------- Styles --------------------------------- */

const LETTER_STYLES: StyleDefinition[] = [
  { id: "bold", label: "Bold", category: "letters", transform: (t) => applyMap(t, BOLD) },
  { id: "boldSans", label: "Bold Sans", category: "letters", transform: (t) => applyMap(t, BOLD_SANS) },
  { id: "italic", label: "Italic", category: "letters", transform: (t) => applyMap(t, ITALIC) },
  { id: "script", label: "Script / Cursive", category: "letters", transform: (t) => applyMap(t, SCRIPT) },
  { id: "scriptBold", label: "Bold Script", category: "letters", transform: (t) => applyMap(t, SCRIPT_BOLD) },
  { id: "fraktur", label: "Gothic / Fraktur", category: "letters", transform: (t) => applyMap(t, FRAKTUR) },
  { id: "doubleStruck", label: "Double-Struck", category: "letters", transform: (t) => applyMap(t, DOUBLE_STRUCK) },
  { id: "smallCaps", label: "Small Caps", category: "letters", transform: (t) => applyMap(t.toLowerCase(), SMALL_CAPS) },
  { id: "bubble", label: "Bubble", category: "letters", transform: (t) => applyMap(t.toUpperCase(), BUBBLE) },
  { id: "bubbleFilled", label: "Bubble (Filled)", category: "letters", transform: (t) => applyMap(t.toUpperCase(), BUBBLE_FILLED) },
  { id: "squared", label: "Squared", category: "letters", transform: (t) => applyMap(t.toUpperCase(), SQUARED) },
  { id: "squaredFilled", label: "Squared (Filled)", category: "letters", transform: (t) => applyMap(t.toUpperCase(), SQUARED_FILLED) },
  { id: "fullwidth", label: "Fullwidth", category: "letters", transform: (t) => applyMap(t, FULLWIDTH) },
  { id: "upsideDown", label: "Upside Down", category: "letters", transform: (t) => [...t].reverse().map((c) => UPSIDE_DOWN_MAP[c] ?? c).join("") },
  { id: "cyrillic", label: "Cyrillic Style", category: "letters", transform: (t) => applyMap(t.toLowerCase(), CYRILLIC_LOOKALIKE) },
  { id: "greek", label: "Greek Style", category: "letters", transform: (t) => applyMap(t.toLowerCase(), GREEK_LOOKALIKE) },
  { id: "thai", label: "Thai Style", category: "letters", transform: (t) => applyMap(t.toLowerCase(), THAI_LOOKALIKE) },
];

const DECORATED_STYLES: StyleDefinition[] = [
  { id: "strikethrough", label: "Strikethrough", category: "decorated", transform: (t) => [...t].map((c) => (c === "\n" ? c : c + "\u0336")).join("") },
  { id: "underline", label: "Underline", category: "decorated", transform: (t) => [...t].map((c) => (c === "\n" ? c : c + "\u0332")).join("") },
  { id: "doubleStrike", label: "Double Strike", category: "decorated", transform: (t) => [...t].map((c) => (c === "\n" ? c : c + "\u0335\u0336")).join("") },
  { id: "circled", label: "Circled Keycap", category: "decorated", transform: withCombining("\u20e3") },
  { id: "boxedCombining", label: "Boxed (combining)", category: "decorated", transform: withCombining("\u20de") },
  { id: "zalgoLight", label: "Glitch (Light)", category: "decorated", transform: (t) => zalgofy(t, 2) },
  { id: "zalgoHeavy", label: "Glitch (Heavy)", category: "decorated", transform: (t) => zalgofy(t, 6) },
];

const CATEGORY_LABELS: Record<Category, string> = {
  letters: "Letter Styles",
  decorated: "Decorated",
  frames: "Borders & Frames",
};
const CATEGORY_HINTS: Record<Category, string> = {
  letters: "Clean, one-for-one letter swaps. Best for names, usernames, and short labels.",
  decorated: "Adds marks to each letter. Strikethrough and underline are reliable; glitch styles can look different across apps.",
  frames: "Wraps your whole text in emoji or symbols. Best for bios and captions, not usernames — these run long.",
};

/* -------------------------------- Component -------------------------------- */

export default function FancyTextGenerator({}: { toolName: string }) {
  const idPrefix = useId();
  const [input, setInput] = useState("sunset edits");
  const [category, setCategory] = useState<Category>("letters");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [zalgoSeed, setZalgoSeed] = useState(0);

  const letterAndDecoratedResults = useMemo(() => {
    if (!input) return [];
    const styles = category === "letters" ? LETTER_STYLES : category === "decorated" ? DECORATED_STYLES : [];
    return styles.map((style) => ({ id: style.id, label: style.label, value: style.transform(input) }));
    // zalgoSeed intentionally triggers a recompute so "Reroll" regenerates glitch output
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, category, zalgoSeed]);

  const frameResults = useMemo(() => {
    if (!input) return [];
    return FRAMES.map((frame) => ({ id: frame.id, label: frame.label, value: frame.wrap(input) }));
  }, [input]);

  const results = category === "frames" ? frameResults : letterAndDecoratedResults;

  async function copyStyle(id: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(null), 1400);
    } catch {
      setCopiedId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor={`${idPrefix}-input`} className="text-sm font-medium text-slate-700">
            Your text
          </label>
          <button
            type="button"
            onClick={() => setInput("")}
            disabled={!input}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Clear
          </button>
        </div>
        <input
          id={`${idPrefix}-input`}
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          maxLength={150}
          placeholder="Type something for your bio, name, or caption…"
          className="w-full rounded-xl border border-slate-300 p-4 text-lg text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
        />
        <p className="mt-1 text-xs text-slate-500">{input.length}/150 characters</p>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Style category">
        {(Object.keys(CATEGORY_LABELS) as Category[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={category === key}
            onClick={() => setCategory(key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              category === key ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {CATEGORY_LABELS[key]}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500">{CATEGORY_HINTS[category]}</p>

      {category === "decorated" && (
        <button
          type="button"
          onClick={() => setZalgoSeed((n) => n + 1)}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-200"
        >
          🎲 Reroll glitch styles
        </button>
      )}

      {results.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          Type something above to see it in every style.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {results.map((result) => (
            <div key={result.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{result.label}</p>
                  <p className="mt-1 break-words text-base font-medium text-slate-900">{result.value}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void copyStyle(result.id, result.value)}
                  aria-label={`Copy ${result.label}`}
                  className="shrink-0 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
                >
                  {copiedId === result.id ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}